import { isAtlasAgentId } from '@core/constant/atlasAgent';
import { isAtlasCoderId } from '@core/constant/atlasCoder';
import { isAtlasNewId } from '@core/constant/atlasNew';
import { isAtlasSystemId } from '@core/constant/atlasSystem';
import { isCodexBotId } from '@core/constant/codexAgent';
import { isCursorBotId } from '@core/constant/cursorAgent';
import AtlasLogo from '@icon/atlas-mark.svg';
import CodexIcon from '@icon/wide-codex-ide.svg';
import CursorIcon from '@icon/wide-cursor-ide.svg';
import type { Component, ComponentProps } from 'solid-js';

/** The brand mark a first-party bot wears wherever an avatar is drawn. */
export type FirstPartyBotMark = {
  Icon: Component<ComponentProps<'svg'>>;
  /** Macro's own marks take the accent color; partner marks stay ink. */
  tone: 'accent' | 'ink';
};

const ATLAS_MARK: FirstPartyBotMark = { Icon: AtlasLogo, tone: 'accent' };
const CURSOR_MARK: FirstPartyBotMark = { Icon: CursorIcon, tone: 'ink' };
const CODEX_MARK: FirstPartyBotMark = { Icon: CodexIcon, tone: 'ink' };

/**
 * The avatar mark for a first-party bot (bare UUID or `bot|<uuid>`). System
 * bots have no `bots` row and therefore no `avatar_url`; their picture is the
 * mark the client ships, so every surface (mention menu, message sender,
 * inbox) resolves it here rather than falling back to an initial or a generic
 * robot. Undefined for team bots, which carry their own avatar.
 */
export function firstPartyBotMark(
  id: string | undefined
): FirstPartyBotMark | undefined {
  if (!id) return undefined;
  if (
    isAtlasAgentId(id) ||
    isAtlasNewId(id) ||
    isAtlasCoderId(id) ||
    isAtlasSystemId(id)
  ) {
    return ATLAS_MARK;
  }
  if (isCursorBotId(id)) return CURSOR_MARK;
  if (isCodexBotId(id)) return CODEX_MARK;
  return undefined;
}

/** Text color class for a mark's tone. */
export function firstPartyBotMarkTone(mark: FirstPartyBotMark): string {
  return mark.tone === 'accent' ? 'text-accent' : 'text-ink';
}
